import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import './HomePage.css';

function HomePage() {
  const [games, setGames] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('bridge-view-mode') || 'card';
  });

  function handleViewMode(mode) {
    setViewMode(mode);
    localStorage.setItem('bridge-view-mode', mode);
  }

  useEffect(() => {
    fetchGames();
  }, []);

  async function fetchGames() {
    try {
      const { data, error } = await supabase
        .from('games')
        .select('*')
        .order('game_date', { ascending: false });

      if (error) throw error;
      setGames(data || []);
    } catch (error) {
      console.error('Error fetching games:', error);
    }
  }

  // Filter games based on search query
  const filteredGames = games.filter(game => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    
    // Search in title
    if (game.title.toLowerCase().includes(query)) return true;
    
    // Search in player names
    if (game.scores) {
      for (const score of game.scores) {
        if (score.players) {
          for (const player of score.players) {
            if (player.toLowerCase().includes(query)) return true;
          }
        }
      }
    }
    
    // Search in director
    if (game.director && game.director.toLowerCase().includes(query)) return true;
    
    return false;
  });

  // Organize games by month
  function organizeGamesByMonth() {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed
    
    // Calculate month boundaries
    const thisMonthStart = new Date(currentYear, currentMonth, 1);
    const lastMonthStart = new Date(currentYear, currentMonth - 1, 1);
    const twoMonthsAgoStart = new Date(currentYear, currentMonth - 2, 1);
    
    const sections = {
      thisMonth: [],
      lastMonth: [],
      twoMonthsAgo: [],
      older: []
    };
    
    filteredGames.forEach(game => {
      const gameDate = new Date(game.game_date);
      
      if (gameDate >= thisMonthStart) {
        sections.thisMonth.push(game);
      } else if (gameDate >= lastMonthStart) {
        sections.lastMonth.push(game);
      } else if (gameDate >= twoMonthsAgoStart) {
        sections.twoMonthsAgo.push(game);
      } else {
        sections.older.push(game);
      }
    });
    
    // Sort each section: first by date (descending), then by title (alphabetically)
    const sortSection = (section) => {
      return section.sort((a, b) => {
        // First sort by date (descending - newest first)
        const dateA = new Date(a.game_date);
        const dateB = new Date(b.game_date);
        if (dateB - dateA !== 0) {
          return dateB - dateA;
        }
        // If dates are equal, sort alphabetically by title
        return a.title.localeCompare(b.title);
      });
    };
    
    sortSection(sections.thisMonth);
    sortSection(sections.lastMonth);
    sortSection(sections.twoMonthsAgo);
    sortSection(sections.older);
    
    return sections;
  }

  function getMonthTitle(monthsAgo) {
    const now = new Date();
    const targetDate = new Date(now.getFullYear(), now.getMonth() - monthsAgo, 1);
    return targetDate.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  }

  const gameSections = organizeGamesByMonth();

  function renderGameCard(game) {
    // Calculate count
    const count = game.scores?.length || 0;
    const countLabel = game.game_type === 'pairs' ? 'pairs' : 'teams';
    
    // Get scoring type - Teams are always IMP, Pairs check metadata
    let scoringType = null;
    if (game.game_type === 'teams') {
      scoringType = 'IMP';
    } else if (game.game_type === 'pairs' && game.metadata?.Scoring) {
      scoringType = game.metadata.Scoring === '2' ? 'IMP' : 'MP';
    }
    
    return (
      <div key={game.id} className="game-card-wrapper">
        <Link to={`/game/${game.id}`} className={`game-card ${game.is_special ? 'special' : ''}`}>
          <h3>{game.title}</h3>
          <div className="game-meta">
            <span className="game-date">
              {new Date(game.game_date).toLocaleDateString('en-GB')}
            </span>
            <span className={`game-type ${game.game_type}`}>
              {game.game_type === 'teams' ? 'Teams' : 'Pairs'}
            </span>
            {scoringType && (
              <span className={`scoring-type ${scoringType.toLowerCase()}`}>
                {scoringType}
              </span>
            )}
            <span className="game-count">
              {count} {countLabel}
            </span>
          </div>
        </Link>
      </div>
    );
  }

  function renderGameRow(game) {
    const count = game.scores?.length || 0;
    const countLabel = game.game_type === 'pairs' ? 'pairs' : 'teams';

    let scoringType = null;
    if (game.game_type === 'teams') {
      scoringType = 'IMP';
    } else if (game.game_type === 'pairs' && game.metadata?.Scoring) {
      scoringType = game.metadata.Scoring === '2' ? 'IMP' : 'MP';
    }

    return (
      <Link key={game.id} to={`/game/${game.id}`} className={`game-list-row ${game.is_special ? 'special' : ''}`}>
        <span className="row-title">{game.title}</span>
        <div className="row-badges">
          <span className="game-date">
            {new Date(game.game_date).toLocaleDateString('en-GB')}
          </span>
          <span className={`game-type ${game.game_type}`}>
            {game.game_type === 'teams' ? 'Teams' : 'Pairs'}
          </span>
          {scoringType && (
            <span className={`scoring-type ${scoringType.toLowerCase()}`}>
              {scoringType}
            </span>
          )}
          <span className="game-count">{count} {countLabel}</span>
        </div>
      </Link>
    );
  }

  return (
    <div className="container">
      <div className="page-header">
        <h2>Bridge Game Results</h2>
        <div className="view-toggle">
          <button
            className={`view-toggle-btn ${viewMode === 'card' ? 'active' : ''}`}
            onClick={() => handleViewMode('card')}
            title="Card view"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <rect x="0" y="0" width="7" height="7" rx="1"/>
              <rect x="9" y="0" width="7" height="7" rx="1"/>
              <rect x="0" y="9" width="7" height="7" rx="1"/>
              <rect x="9" y="9" width="7" height="7" rx="1"/>
            </svg>
          </button>
          <button
            className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => handleViewMode('list')}
            title="List view"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <rect x="0" y="1" width="16" height="2.5" rx="1.25"/>
              <rect x="0" y="6.75" width="16" height="2.5" rx="1.25"/>
              <rect x="0" y="12.5" width="16" height="2.5" rx="1.25"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="search-filter">
        <input
          type="text"
          placeholder="Search by event name or player name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="clear-search">
            Clear
          </button>
        )}
      </div>
      
      {filteredGames.length === 0 ? (
        <p>{searchQuery ? 'No games match your search.' : 'No games uploaded yet. Visit the Admin page to upload results.'}</p>
      ) : (
        <div className="games-organized">
          {gameSections.thisMonth.length > 0 && (
            <div className="games-section">
              <h3 className="section-title">{getMonthTitle(0)}</h3>
              <div className={`games-list ${viewMode === 'list' ? 'list-view' : ''}`}>
                {gameSections.thisMonth.map(viewMode === 'list' ? renderGameRow : renderGameCard)}
              </div>
            </div>
          )}

          {gameSections.lastMonth.length > 0 && (
            <div className="games-section">
              <h3 className="section-title">{getMonthTitle(1)}</h3>
              <div className={`games-list ${viewMode === 'list' ? 'list-view' : ''}`}>
                {gameSections.lastMonth.map(viewMode === 'list' ? renderGameRow : renderGameCard)}
              </div>
            </div>
          )}

          {gameSections.twoMonthsAgo.length > 0 && (
            <div className="games-section">
              <h3 className="section-title">{getMonthTitle(2)}</h3>
              <div className={`games-list ${viewMode === 'list' ? 'list-view' : ''}`}>
                {gameSections.twoMonthsAgo.map(viewMode === 'list' ? renderGameRow : renderGameCard)}
              </div>
            </div>
          )}

          {gameSections.older.length > 0 && (
            <div className="games-section">
              <h3 className="section-title">Previous Events</h3>
              <div className={`games-list ${viewMode === 'list' ? 'list-view' : ''}`}>
                {gameSections.older.map(viewMode === 'list' ? renderGameRow : renderGameCard)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default HomePage;
